export type UiTheme = 'light' | 'dark';

export const UI_THEME_STORAGE_KEY = 'uiTheme';
export const UI_FONT_STORAGE_KEY = 'uiFont';

export type UiFont = 'web' | 'system';

function isUiTheme(value: unknown): value is UiTheme {
  return value === 'light' || value === 'dark';
}

function isUiFont(value: unknown): value is UiFont {
  return value === 'web' || value === 'system';
}

export function getSystemUiTheme(): UiTheme {
  if (typeof window === 'undefined') return 'light';
  if (!window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function getSavedUiTheme(): UiTheme | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(UI_THEME_STORAGE_KEY);
    return isUiTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function getSavedUiFont(): UiFont | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(UI_FONT_STORAGE_KEY);
    return isUiFont(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function applyUiTheme(theme: UiTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-ui-theme', theme);
  // Нативные элементы (скроллбары/инпуты) подстраиваются под тему
  document.documentElement.style.colorScheme = theme;
}

export function applyUiFont(font: UiFont): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-ui-font', font);
}

export function initUiTheme(): UiTheme {
  const theme = getSavedUiTheme() ?? getSystemUiTheme();
  applyUiTheme(theme);
  return theme;
}

export function initUiFont(): UiFont {
  const font = getSavedUiFont() ?? 'web';
  applyUiFont(font);
  return font;
}

export function initUiThemeAndFont(): { theme: UiTheme; font: UiFont } {
  const theme = initUiTheme();
  const font = initUiFont();
  return { theme, font };
}

export function setUiTheme(theme: UiTheme): void {
  applyUiTheme(theme);
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(UI_THEME_STORAGE_KEY, theme);
  } catch {
    // Игнорируем ошибки (например, privacy режим)
  }
}

export function setUiFont(font: UiFont): void {
  applyUiFont(font);
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(UI_FONT_STORAGE_KEY, font);
  } catch {
    // Игнорируем ошибки (например, privacy режим)
  }
}

/**
 * Получает текущую тему из DOM атрибутов
 */
export function getCurrentUiThemeFromDom(): UiTheme {
  if (typeof document === 'undefined') return 'light';
  const theme = document.documentElement.getAttribute('data-ui-theme');
  return theme === 'dark' ? 'dark' : 'light';
}

/**
 * Получает текущий шрифт из DOM атрибутов
 */
export function getCurrentUiFontFromDom(): UiFont {
  if (typeof document === 'undefined') return 'web';
  const font = document.documentElement.getAttribute('data-ui-font');
  return font === 'system' ? 'system' : 'web';
}




